import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Loader2, MapPin, Wind } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api } from "./api";
import FacadeStudio from "./FacadeStudio";
import SystemsDesigner from "./SystemsDesigner";
import SectionsPanel from "./SectionsPanel";
import ModelTakeoff from "./ModelTakeoff";

export default function ProjectPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("studio");
  const [openDesign, setOpenDesign] = useState(null);

  useEffect(() => {
    api.get(`/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => setError(true));
  }, [projectId]);

  if (error) return <div data-testid="project-not-found" className="text-zinc-400">Project not found. <Link to="/dashboard" className="text-sky-400">Back to projects</Link></div>;
  if (!project) return <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />;

  return (
    <div>
      <Link to="/dashboard" data-testid="back-to-projects" className="mb-4 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white"><ChevronLeft className="h-4 w-4" />Projects</Link>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-amber-400">{project.building_type}</div>
          <h1 className="mt-1 font-[Manrope] text-3xl font-extrabold tracking-tight md:text-4xl" data-testid="project-title">{project.name}</h1>
          <div className="mt-2 flex flex-wrap gap-4 font-mono text-xs text-zinc-500">
            {project.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{project.location}</span>}
            <span className="inline-flex items-center gap-1"><Wind className="h-3.5 w-3.5" />Vb {project.basic_wind_speed} m/s · H {project.building_height_m} m · TC{project.terrain_category}</span>
          </div>
        </div>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6 h-auto flex-wrap border border-zinc-800 bg-[#121215]">
          <TabsTrigger value="studio" data-testid="tab-facade-studio">Facade Studio</TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-sections">Sections</TabsTrigger>
          <TabsTrigger value="model" data-testid="tab-3d-model">3D Model</TabsTrigger>
          <TabsTrigger value="systems" data-testid="tab-systems-designer"><span className="sm:hidden">Systems</span><span className="hidden sm:inline">Curtain Wall · Skylight · Canopy</span></TabsTrigger>
        </TabsList>
        <TabsContent value="studio"><FacadeStudio project={project} setProject={setProject} onOpenDesign={(d) => { setOpenDesign(d); setTab("systems"); }} /></TabsContent>
        <TabsContent value="sections"><SectionsPanel project={project} setProject={setProject} /></TabsContent>
        <TabsContent value="model" forceMount className="focus-visible:outline-none focus-visible:ring-0 data-[state=inactive]:hidden"><ModelTakeoff project={project} setProject={setProject} onOpenDesign={(d) => { setOpenDesign(d); setTab("systems"); }} /></TabsContent>
        <TabsContent value="systems"><SystemsDesigner project={project} openDesign={openDesign} /></TabsContent>
      </Tabs>
    </div>
  );
}
